<template>
  <popup-dialog
    :visible="visible"
    :close-button-visible="true"
    y-alignment="top"
    class="model-search-popup"
    header-class="model-search-popup__searchbar"
    @close="close"
    @show="onShow"
  >
    <template #header>
      <input-text
        ref="searchBar"
        v-model:value="state.searchText"
        style="width: calc(100% - 3rem)"
        :placeholder="$t('labels.filterEntries')"
        @update:value="onSearchUpdate()"
      />
    </template>

    <div>
      <div v-if="state.searchText.length > 0" class="p-4 pt-1">
        <p v-if="state.searching" class="text-center">
          {{ $t("messages.loadingData") }}
        </p>
        <p v-else-if="state.models.length == 0" class="text-center">
          {{ $t("errors.noEntries") }}
        </p>

        <div class="column gap-4 column--container">
          <model-entry
            v-for="model in state.models"
            :key="model.id"
            :model="model"
            :location-visible="true"
            :description-visible="true"
            @location-change="close()"
          />
        </div>
      </div>
    </div>
  </popup-dialog>
</template>

<script setup lang="ts">
import { ref, reactive } from "vue";
import { debounce } from "lodash-es";

import PopupDialog from "@core/ui/PopupDialog.vue";
import InputText from "@core/input/InputText.vue";
import ModelEntry from "./ModelEntry.vue";

import { modelAPI } from "@/api/modelAPI";
import { useToast } from "vue-toastification";
import { useWorkspaces } from "@/api/workspaces.store";
import { useSharedModels } from "@/api/sharedModels.store";

import type { Model } from "@/api/Model";
import type { SharedModel } from "@/api/SharedModel";

interface Props {
  visible: boolean;
}
defineProps<Props>();
const emit = defineEmits<{
  (e: "close"): void;
}>();

const toast = useToast();
const { loadWorkspaces } = useWorkspaces();
const { loadSharedModels } = useSharedModels();

const searchBar = ref<InstanceType<typeof InputText>>();

const state = reactive({
  searching: false,
  searchText: "",
  models: [] as (Model | SharedModel)[]
});

function close() {
  console.log("close search popup");
  emit("close");
}

function onShow() {
  state.searchText = "";
  state.models = [];
  searchBar.value?.focus();
  loadWorkspaces();
}

async function search() {
  const searchText = state.searchText;
  if (searchText == "") {
    state.models = [];
    state.searching = false;
    return;
  }

  try {
    const [models, shares] = await Promise.all([
      modelAPI.list({ query: { search: searchText } }),
      loadSharedModels({ query: { search: searchText } })
    ]);
    const result = [...shares, ...models].sort((a, b) => {
      if (a.modified > b.modified) return -1;
      if (a.modified < b.modified) return 1;
      return 0;
    });
    state.models = result;
    return result;
  } catch (error) {
    if (error instanceof Error) {
      toast.error(error.message);
    }
    console.log(toast);
    return [];
  } finally {
    state.searching = false;
  }
}

const throttledSearch = debounce(search, 700, {
  leading: false,
  trailing: true
});

function onSearchUpdate() {
  state.searching = true;
  throttledSearch();
}
</script>

<style lang="scss">
.model-search-popup {
  max-height: calc(100vh - 80px);
  border-radius: 10px;

  &__searchbar {
    position: sticky;
    top: 0;
    padding-block: 1rem;
    padding-inline: 1rem;
    background-color: var(--color-background-deep);
    box-shadow: 0 0 0.5rem var(--color-background-deep);
  }
}
</style>
